import { EnergyFlowChartNodeItem, EnergyFlowChartResponse } from '@/api/plustekSankey';
import { formatCompositeNumWithUnit, formatMediaNumWithUnit } from '../../utils';
import { getEmpty } from '../../shared';
import { EchartsWithTheme, Key, Themes, useTheme } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { useMemo } from 'react';
import { apiV2EnergyAnalysisGetDifferenceDescPost } from '@maxtropy/device-customer-apis-v2';
import { keepPercentOrLine } from '@/utils/utils';
// import { chartHeight, chartOption } from './mock';
// import { chartHeight2, chartOption2 } from './mock2';

interface Props {
  data?: EnergyFlowChartResponse;
  physicalUnitName?: string;
  isMedium: boolean;
  setModalOpen: (open: boolean) => void;
  setChartSelectedUnitId: (id: Key) => void;
}

const color = [
  'var(--mx-diagram15-color)',
  'var(--mx-diagram1-color)',
  'var(--mx-diagram6-color)',
  'var(--mx-diagram18-color)',
  'var(--mx-diagram9-color)',
];

export const formatNum = (num: number, isMedium: boolean, unit?: string, fixed?: number) => {
  return isMedium ? formatMediaNumWithUnit(num, unit, fixed) : formatCompositeNumWithUnit(num, fixed);
};

export const getNodeMaxDepth = (nodes: EnergyFlowChartNodeItem[]) => {
  const all_depth = nodes.map(i => i.depth);
  return Math.max(...all_depth);
};

// 获取每一层的节点数量
const getNodesCountByDepth = (nodes: EnergyFlowChartNodeItem[]) => {
  const depthMap: Record<number, number> = {};
  nodes.forEach(node => {
    if (depthMap[node.depth]) {
      depthMap[node.depth]++;
    } else {
      depthMap[node.depth] = 1;
    }
  });
  return depthMap;
};

// 获取最多节点的层数量
const getMaxNodesInAnyLevel = (nodes: EnergyFlowChartNodeItem[]) => {
  const depthMap = getNodesCountByDepth(nodes);
  return Math.max(...Object.values(depthMap));
};

// 根据最多节点的层计算图表高度
export const getSankeyHeight = (nodes: EnergyFlowChartNodeItem[], scale: number = 1) => {
  const maxNodesCount = getMaxNodesInAnyLevel(nodes);

  const nodeHeight = 10; // 每个节点高度
  const gap = 8; // 节点之间间距
  // const padding = 12;   // 上下留白
  const h = maxNodesCount * (nodeHeight + gap);
  return h * scale;
};

const UnitSankey: React.FC<Props> = ({ data, physicalUnitName, isMedium, setModalOpen, setChartSelectedUnitId }) => {
  const chartHeight = useMemo(() => {
    const height = getSankeyHeight(data?.nodes ?? []);
    return height;
  }, [data]);

  const theme = useTheme();
  const { data: differenceDesc } = useRequest(apiV2EnergyAnalysisGetDifferenceDescPost);

  const chartOption = useMemo(() => {
    return {
      toolbox: {
        top: 0,
        feature: {
          saveAsImage: {
            title: '保存图片',
            name: '能流图',
            backgroundColor: theme === Themes.DARK ? '#000' : '#fff',
          },
        },
      },
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        borderColor: 'transparent',
        formatter: (v: any) => {
          let res = '';
          if (v.dataType === 'node') {
            res = `<div>
          <p>${v.data.energyUnitName}</p>
          <p style="color: var(--mx-text-base-color); margin-bottom: 0px;" >${formatNum(
            v.value,
            isMedium,
            physicalUnitName
          )}</p>
          ${
            v?.data?.depth !== 0
              ? ` <p style="color: var(--mx-text-base-color);margin-top:8px; margin-bottom: 0px;">
                占比：${keepPercentOrLine(v.data?.proportion)}%
                </p>`
              : ''
          }
          </div>`;
          }
          if (v.dataType === 'edge') {
            const sourceName = data?.nodes?.find(i => i.key === v.data.source)?.energyUnitName;
            const targetName = data?.nodes?.find(i => i.key === v.data.target)?.energyUnitName;
            res = `<div>
          <p>${sourceName}——${targetName}</p>
          <p style="color: var(--mx-text-base-color);margin-bottom: 0px;" >${formatNum(
            v.value,
            isMedium,
            physicalUnitName
          )}</p>
          <p style="color:var(--mx-text-base-color);margin-top:8px;margin-bottom: 0px;" >下级占比：${keepPercentOrLine(
            v.data?.proportion
          )}%</p>
          </div>`;
          }

          return res;
        },
      },
      series: [
        {
          type: 'sankey',
          left: 10,
          right: 10,
          top: 40,
          bottom: 10,
          nodeGap: 8,
          layoutIterations: 0, // 解决能流图衔接问题
          nodeAlign: 'justify',
          label: {
            color: 'var(--mx-text-base-color)',
            rich: {
              a: {
                color: 'var(--mx-error-color)',
              },
              b: {
                color: 'var(--mx-success-color)',
              },
              c: {
                color: 'var(--mx-diagram5-color)',
                marginLeft: 4,
              },
            },
            formatter: (v: any) => {
              if (v.data.energyUnitName === (differenceDesc?.downstreamGreaterThanUpstream || '用能损耗')) {
                return `{a|${v.data.energyUnitName}}{c| ${
                  v.data.depth !== 0 ? keepPercentOrLine(v.data?.proportion) + '%' : ''
                }}`;
              }
              if (v.data.energyUnitName === (differenceDesc?.upstreamGreaterThanDownstream || '未知能耗')) {
                return `{b|${v.data.energyUnitName}}{c| ${
                  v.data.depth !== 0 ? keepPercentOrLine(v.data?.proportion) + '%' : ''
                }}`;
              }
              return `${v.data.energyUnitName}{c| ${
                v.data.depth !== 0 ? keepPercentOrLine(v.data?.proportion) + '%' : ''
              }}`;
            },
          },
          labelLayout: {
            hideOverlap: true,
          },
          draggable: false,
          data: data?.nodes?.map(i => {
            return {
              ...i,
              itemStyle: {
                color:
                  i.energyUnitName === (differenceDesc?.upstreamGreaterThanDownstream || '用能损耗')
                    ? '#ABD335'
                    : i.energyUnitName === (differenceDesc?.downstreamGreaterThanUpstream || '未知能耗')
                    ? '#FF4D4F'
                    : color[i.depth % color.length],
              },
              label: {
                position: i.depth === getNodeMaxDepth(data?.nodes) ? 'left' : 'right',
              },
            };
          }),
          links: data?.links?.map(i => {
            return {
              ...i,
              lineStyle: {
                color: 'gradient',
                opacity: 0.3,
                // curveness: 0.5, // sankey图边曲度，尽量不要设置，在苹果电脑上展示会有问题
              },
            };
          }),
        },
      ],
    };
  }, [data, physicalUnitName, isMedium, theme, differenceDesc]);

  const isEmptyChart = useMemo(() => {
    const all_depth = (data?.nodes ?? []).map(i => i.depth);
    const max_depth = Math.max(...all_depth);
    const min_depth = Math.min(...all_depth);
    return data?.nodes.length === 0 || data?.links.length === 0 || max_depth === min_depth;
  }, [data]);

  return isEmptyChart ? (
    getEmpty()
  ) : (
    <div
      style={{
        width: '100%',
        height: 'calc(100vh - 386px)',
        minHeight: '500px',
        marginTop: 24,
        overflow: 'auto',
      }}
    >
      <div style={{ width: '100%', height: chartHeight, minHeight: '500px' }}>
        <EchartsWithTheme
          theme={theme}
          style={{ width: '100%', height: '100%' }}
          option={chartOption}
          onEvents={{
            click: (v: any) => {
              if (
                v.dataType === 'node' &&
                v.data.energyUnitName !== (differenceDesc?.downstreamGreaterThanUpstream || '未知能耗') &&
                v.data.energyUnitName !== (differenceDesc?.upstreamGreaterThanDownstream || '用能损耗')
              ) {
                setModalOpen(true);
                setChartSelectedUnitId(v.data.energyUnitId);
              }
            },
          }}
        />
      </div>
    </div>
  );
};

export default UnitSankey;
